import { Box } from '@chakra-ui/react';
import { CountdownTimer } from '@diamond/shared/ui';
import {
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import Link from 'next/link';
import { memo, useEffect, useState } from 'react';

import { resolveMyOrderPaymentDestination } from './resolve-my-order-payment-destination';
import { PaymentActionsProps } from './types';

const normalizeExpiryMs = (expiredAt: number | null) => {
  if (!expiredAt) return null;
  if (expiredAt < 1_000_000_000_000) return expiredAt * 1000;
  return expiredAt;
};

const PaymentActions = memo(
  ({ data, selectedShipToId, isMobile }: PaymentActionsProps) => {
    const isPaymentInstructionEnabled = useFeatureFlag(
      LIST_FEATURE_FLAGS.FF_PAYMENT_INSTRUCTION_PAGE,
      false
    );
    const destination = resolveMyOrderPaymentDestination({
      data,
      isPaymentInstructionEnabled,
      warehouse: selectedShipToId,
    });
    const expiryMs = normalizeExpiryMs(data.expired_at);
    const [isExpired, setIsExpired] = useState(
      () => expiryMs !== null && expiryMs <= Date.now()
    );

    useEffect(() => {
      if (expiryMs === null) {
        setIsExpired(false);
        return undefined;
      }

      const remainingMs = expiryMs - Date.now();

      if (remainingMs <= 0) {
        setIsExpired(true);
        return undefined;
      }

      setIsExpired(false);
      const timeoutId = window.setTimeout(
        () => setIsExpired(true),
        remainingMs
      );

      return () => window.clearTimeout(timeoutId);
    }, [expiryMs]);

    if (
      data.status !== 'waiting_for_payment' ||
      isExpired ||
      (isPaymentInstructionEnabled && destination.kind === 'order_detail')
    ) {
      return null;
    }

    const uploadLink = destination.href;
    const handlePaymentAction = () => {
      trackEvent(TRACKING_EVENTS.CLICK_UPLOAD_PAYMENT_PROOF, {
        entry_source: 'my-order',
        ff_payment_instruction_page: isPaymentInstructionEnabled,
        order_meta_id: data.order_meta_id,
        payment_method: 'bank_transfer',
        platform: 'web',
        resolved_destination: destination.kind,
        warehouse: selectedShipToId,
      });

      if (!isPaymentInstructionEnabled) return;

      trackEvent(TRACKING_EVENTS.WFP_ROUTE_RESOLVED, {
        entry_source: 'my-order',
        ff_payment_instruction_page: true,
        order_meta_id: data.order_meta_id,
        payment_sub_state: data.payment_sub_state,
        platform: 'web',
        presentation: 'page',
        resolved_destination: destination.kind,
        source: 'my_order',
        sub_state: data.payment_sub_state,
        warehouse: selectedShipToId,
      });
    };

    if (isMobile) {
      return (
        <Box
          w="full"
          display="flex"
          justifyContent="center"
          textAlign="center"
          gap="2.5"
          my="2"
          mb="4"
        >
          <Box
            as={Link}
            fontSize="xs"
            color="blue"
            whiteSpace="nowrap"
            textDecoration="underline"
            fontWeight="600"
            href={uploadLink}
            onClick={handlePaymentAction}
          >
            Unggah Bukti Transfer
          </Box>
          {data.expired_at && <CountdownTimer endTime={data.expired_at} />}
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        flexDir="row"
        alignItems="center"
        justifyContent="center"
        gap="2.5"
        w="full"
      >
        <Box
          as={Link}
          fontSize="md"
          color="blue"
          whiteSpace="nowrap"
          textDecoration="underline"
          fontWeight="600"
          href={uploadLink}
          onClick={handlePaymentAction}
        >
          Unggah Bukti Transfer
        </Box>
        {data.expired_at && <CountdownTimer endTime={data.expired_at} />}
      </Box>
    );
  }
);

PaymentActions.displayName = 'PaymentActions';

export { PaymentActions };
